(() => {
  'use strict';
  function mount(options = {}) {
  const root = options.root || document;
  const surface = options.surface || document.documentElement;
  const lifecycle = new AbortController();
  const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: lifecycle.signal });
  const $ = id => root.getElementById(id);
  const storagePrefix = options.storagePrefix || 'bullseye-originals-record-bin-preview';
  const FAVORITES_KEY = storagePrefix + ':favorites:v1';
  const POSITION_KEY = storagePrefix + ':position:v1';
  const MOTION_KEY = storagePrefix + ':motion:v1';
  const { clamp, gestureVelocity } = window.BullseyeNavigation;
  const catalog = (options.catalog || window.BULLSEYE_CATALOG)?.products;
  if (!Array.isArray(catalog) || !catalog.length || new Set(catalog.map(p => p.id)).size !== catalog.length) {
    $('catalog-error').hidden = false;
    $('fan-view').hidden = true;
    $('catalog-total').textContent = 'Catalog unavailable';
    $('reload').onclick = () => location.reload();
    return;
  }
  const byId = new Map(catalog.map(p => [p.id, p]));
  const humanize = tag => tag.replace(/^(subject|theme)-/, '').replaceAll('-and-', ' & ').replaceAll('-', ' ').replace(/\b\w/g, c => c.toUpperCase());
  const announce = text => { $('announcement').textContent = text; };
  const storageNotice = () => { $('storage-notice').hidden = false; };
  let favorites = new Set();
  let saved = {};
  try {
    const ids = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    if (Array.isArray(ids)) favorites = new Set(ids.filter(id => byId.has(id)));
  } catch { storageNotice(); }
  try { saved = JSON.parse(sessionStorage.getItem(POSITION_KEY) || '{}') || {}; } catch { /* Memory-only browsing. */ }
  const subjects = [...new Set(catalog.flatMap(p => p.tags.filter(t => t.startsWith('subject-'))))].sort();
  const themes = [...new Set(catalog.flatMap(p => p.tags.filter(t => t.startsWith('theme-'))))].sort();
  let subject = subjects.includes(saved.subject) ? saved.subject : '';
  let theme = themes.includes(saved.theme) ? saved.theme : '';
  let query = typeof saved.query === 'string' ? saved.query.slice(0, 200) : '';
  let selectedId = byId.has(saved.id) ? saved.id : catalog[Math.min(3, catalog.length - 1)].id;
  // Each visit opens the gallery; retain the selected design and filters only.
  let mode = 'fan';
  let matchingIds = new Set(), hasFilters = false;
  let filtered = [], index = 0, drag = null, suppressClickUntil = 0;
  let searchTimer, scrollFrame, dragFrame, warmTimer;
  let cardGap = 95, visualPosition = 0;
  const cardCache = new Map();
  let activeGlide = false, renderedIds = '';
  const imageFailures = new Set();
  const filterPositions = new Map();
  const stage = $('stage');
  const track = document.createElement('div');
  track.className = 'disc-track';
  stage.append(track);
  stage.classList.add('continuous-fan');
  surface.classList.add('pointer-input');
  listen(document, 'pointerdown', () => surface.classList.add('pointer-input'));
  listen(document, 'keydown', e => {
    if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) surface.classList.remove('pointer-input');
  });
  const mediaMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let manualMotion = false;
  try { manualMotion = localStorage.getItem(MOTION_KEY) === 'true'; } catch { /* Default to system. */ }
  function setMotion() {
    const reduced = mediaMotion.matches || manualMotion;
    if (reduced && activeGlide) { stopGlide(); renderFan(); }
    surface.classList.toggle('less-motion', reduced);
    $('less-motion').checked = reduced;
    $('less-motion').disabled = mediaMotion.matches;
    $('less-motion').title = mediaMotion.matches ? 'Reduced motion follows your device setting.' : 'Use instant, quiet transitions.';
  }
  listen(mediaMotion, 'change', setMotion);
  $('less-motion').addEventListener('change', e => {
    manualMotion = e.target.checked;
    try { localStorage.setItem(MOTION_KEY, String(manualMotion)); } catch { /* Optional setting. */ }
    setMotion();
  });
  setMotion();
  const filterKey = () => JSON.stringify([subject, theme, query]);
  function persistPosition() {
    try { sessionStorage.setItem(POSITION_KEY, JSON.stringify({ id: selectedId, subject, theme, query, mode })); } catch { /* Browsing still works. */ }
  }
  function imageURL(product, width) {
    // All URLs come from the captured real catalog. Never manufacture artwork.
    const url = new URL(product.image.url);
    if (url.protocol !== 'https:' || !['cdn.shopify.com', ...(options.imageHosts || [])].includes(url.hostname)) return '';
    url.searchParams.set('width', String(width));
    return url.href;
  }
  function artwork(product, size = 640, eager = false) {
    const wrap = document.createElement('span');
    wrap.className = 'artwork loading';
    wrap.dataset.crop = product.crop;
    const status = document.createElement('span');
    status.className = 'art-status';
    status.setAttribute('aria-hidden', 'true');
    const kicker = document.createElement('span');
    const title = document.createElement('strong');
    const note = document.createElement('small');
    kicker.textContent = `Original No. ${product.number}`;
    title.textContent = product.title;
    note.textContent = 'Loading artwork…';
    status.append(kicker, title, note);
    wrap.append(status);
    const failed = () => {
      imageFailures.add(product.id);
      wrap.className = 'artwork failed';
      note.textContent = 'Artwork unavailable';
      $('image-notice').hidden = false;
    };
    if (imageFailures.has(product.id)) { failed(); return wrap; }
    const img = document.createElement('img');
    img.alt = product.image.altText || product.title;
    img.width = product.image.width || 1024;
    img.height = product.image.height || 1024;
    img.loading = eager ? 'eager' : 'lazy';
    img.decoding = 'async';
    img.draggable = false;
    if (product.image.crop) {
      for (const [key, property] of [['w','width'],['h','height'],['x','left'],['y','top']]) {
        const value = product.image.crop[key];
        if (/^-?\d+(?:\.\d+)?%$/.test(value)) img.style[property] = value;
      }
    }
    img.fetchPriority = eager ? 'high' : 'low';
    img.addEventListener('load', () => { wrap.className = 'artwork loaded'; }, { once: true });
    img.addEventListener('error', failed, { once: true });
    img.src = imageURL(product, size);
    if (size >= 640) {
      img.srcset = [320, 640, 960].map(w => `${imageURL(product, w)} ${w}w`).join(', ');
      img.sizes = size > 640 ? '(max-width: 700px) 70vw, 350px' : '(max-width: 700px) 65vw, 365px';
    }
    wrap.append(img);
    return wrap;
  }
  function makeChip(value, label) {
    const button = document.createElement('button');
    button.className = 'chip';
    button.textContent = label;
    button.dataset.subject = value;
    button.setAttribute('aria-pressed', String(subject === value));
    button.addEventListener('click', () => changeFilters(value, theme, query));
    return button;
  }
  $('category-chips').append(makeChip('', 'All Originals'), ...subjects.map(t => makeChip(t, humanize(t))));
  themes.forEach(t => { const option = new Option(humanize(t), t); $('theme-filter').add(option); });
  $('theme-filter').value = theme;
  $('search').value = query;
  $('catalog-total').textContent = `${catalog.length} designs to discover`;
  function changeFilters(nextSubject, nextTheme, nextQuery) {
    filterPositions.set(filterKey(), selectedId);
    subject = nextSubject; theme = nextTheme; query = nextQuery;
    applyFilters(true);
  }
  function clearFilters() {
    clearTimeout(searchTimer);
    $('search').value = '';
    $('theme-filter').value = '';
    changeFilters('', '', '');
  }
  function applyFilters(resetSelection = false) {
    stopGlide(); cancelDrag(false);
    if (resetSelection) selectedId = null;
    const term = query.trim().toLocaleLowerCase();
    const number = term.match(/^(?:#|bo-|no\.?\s*)?(\d{1,3})$/i);
    const matches = catalog.filter(p => (!subject || p.tags.includes(subject)) && (!theme || p.tags.includes(theme)) && (!term || (number ? Number(p.number) === Number(number[1]) : p.title.toLocaleLowerCase().includes(term))));
    hasFilters = Boolean(subject || theme || term);
    matchingIds = new Set(matches.map(p => p.id));
    filtered = hasFilters ? [...matches, ...catalog.filter(p => !matchingIds.has(p.id))] : [...catalog];
    index = Math.max(0, filtered.findIndex(p => p.id === selectedId));
    if (filtered.length) selectedId = filtered[index].id;
    $('category-chips').querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.subject === subject)));
    $('theme-filter').value = theme;
    if ($('subject-filter')) $('subject-filter').value = subject;
    $('clear-filters').hidden = !subject && !theme && !term;
    $('filtered-count').textContent = hasFilters ? (matches.length ? `${matches.length} matches first · ${catalog.length} designs to explore` : 'No matches · Explore all Originals') : '';
    $('empty-results').hidden = filtered.length > 0;
    $('fan-view').hidden = !filtered.length || mode !== 'fan';
    $('grid-view').hidden = !filtered.length || mode !== 'grid';
    if (filtered.length) { renderFan(); if (mode === 'grid') renderGrid(); }
    persistPosition();
    announce(`${hasFilters ? `${matches.length} matches first. ` : ''}${filtered.length} coasters to explore. ${filtered.length ? filtered[index].title : 'No designs available.'}`);
  }
  // One canvas owns the moving fan. DOM, images and layout stay still while
  // the same fractional position controls every coaster's scale and overlap.
  const canvas = document.createElement('canvas');
  canvas.className = 'fan-canvas'; canvas.setAttribute('aria-hidden', 'true');
  stage.append(canvas);
  const context = canvas.getContext('2d', { alpha: true });
  const sprites = new Map();
  const decodedImages = new WeakSet();
  const decodingImages = new WeakSet(), pendingSprites = new Map();
  let spriteJob = null, imagePaintFrame = 0;
  function pauseSpriteWork() {
    if (!spriteJob) return;
    if (spriteJob.idle) cancelIdleCallback(spriteJob.id);
    else clearTimeout(spriteJob.id);
    spriteJob = null;
  }
  function scheduleSpriteWork() {
    if (spriteJob || !pendingSprites.size || lifecycle.signal.aborted) return;
    const run = () => {
      spriteJob = null;
      if (!pendingSprites.size || lifecycle.signal.aborted) return;
      let nearest = null, nearestDistance = Infinity;
      for (const entry of pendingSprites) {
        const distance = Math.abs(Number(cardCache.get(entry[0])?.dataset.index) - visualPosition);
        if (nearest === null || distance < nearestDistance) { nearest = entry; nearestDistance = distance; }
      }
      const [id, product] = nearest;
      pendingSprites.delete(id);
      if (cardCache.get(id)?.isConnected) spriteFor(product);
      if (!imagePaintFrame) imagePaintFrame = requestAnimationFrame(() => {
        imagePaintFrame = 0;
        if (!lifecycle.signal.aborted) drawFan();
      });
      scheduleSpriteWork();
    };
    // Prepare one nearby image per idle task, including during long flicks.
    spriteJob = typeof requestIdleCallback === 'function'
      ? { idle: true, id: requestIdleCallback(run, { timeout: 250 }) }
      : { idle: false, id: setTimeout(run, 16) };
  }
  function prepareImage(product, img) {
    const ready = () => {
      if (lifecycle.signal.aborted || !cardCache.get(product.id)?.isConnected) return;
      decodedImages.add(img);
      if (!sprites.has(product.id)) pendingSprites.set(product.id, product);
      scheduleSpriteWork();
    };
    if (decodedImages.has(img)) { ready(); return; }
    if (decodingImages.has(img)) return;
    decodingImages.add(img);
    img.decode().then(ready).catch(() => {}).finally(() => decodingImages.delete(img));
  }
  let stageWidth = 0, stageHeight = 0, discWidth = 0, glideFrame = 0;
  function measureStage(width = stage.clientWidth) {
    if (!width) return;
    stageWidth = width; stageHeight = stage.clientHeight;
    cardGap = width < 700 ? Math.min(95, width * .23) : Math.min(140, width * .125);
    discWidth = width < 700 ? Math.min(innerWidth * .65, 275) : 344;
    stage.style.setProperty('--fan-disc', `${discWidth}px`);
    const ratio = Math.min(devicePixelRatio || 1, 2);
    const pixelWidth=Math.round(width*ratio), pixelHeight=Math.round(stageHeight*ratio);
    if(canvas.width!==pixelWidth||canvas.height!==pixelHeight){canvas.width=pixelWidth;canvas.height=pixelHeight;}
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }
  function cardFor(product) {
    let card = cardCache.get(product.id);
    if (!card) {
      card = document.createElement('button'); card.className = 'disc'; card.tabIndex = -1;
      card.dataset.productId = product.id; card.append(artwork(product, 640, false));
      const img = card.querySelector('img');
      img?.addEventListener('load', () => prepareImage(product, img), { once: true });
      cardCache.set(product.id, card);
    }
    return card;
  }
  function spriteFor(product) {
    if (sprites.has(product.id)) return sprites.get(product.id);
    const img = cardCache.get(product.id)?.querySelector('img');
    if (!img?.complete || !img.naturalWidth || !decodedImages.has(img)) return null;
    const size = 640, pad = 20, sprite = document.createElement('canvas');
    sprite.width = sprite.height = size + pad * 2;
    const c = sprite.getContext('2d');
    c.save(); c.translate(pad, pad);
    c.shadowColor = '#48311b26'; c.shadowBlur = 12; c.shadowOffsetY = 8;
    c.fillStyle = '#f7edda'; c.beginPath(); c.arc(size/2,size/2,size/2,0,Math.PI*2); c.fill();
    c.shadowColor = 'transparent'; c.clip();
    const legacy = product.crop === 'legacy';
    const percent = (value, fallback) => value ? parseFloat(value) / 100 : fallback;
    const crop = product.image.crop || {};
    const w = percent(crop.w, legacy ? 1.13636 : 1), h = percent(crop.h, legacy ? 1.13636 : 1);
    const x = percent(crop.x, legacy ? -.06818 : 0), y = percent(crop.y, legacy ? -.06818 : 0);
    c.drawImage(img, x*size, y*size, w*size, h*size); c.restore();
    sprites.set(product.id, sprite); return sprite;
  }
  function warmNearby() {
    if (warmTimer) return;
    warmTimer = setTimeout(() => {
      warmTimer = null;
      const nearby=filtered.slice(Math.max(0,index-12),index+13), keep=new Set(nearby.map(p=>p.id));
      for(const id of sprites.keys())if(!keep.has(id))sprites.delete(id);
      for(const id of pendingSprites.keys())if(!keep.has(id))pendingSprites.delete(id);
      for(const [id,card] of cardCache)if(!keep.has(id)&&!card.isConnected)cardCache.delete(id);
      for (const product of nearby) {
        const img = cardFor(product).querySelector('img');
        if (img) { img.loading = 'eager'; prepareImage(product, img); }
      }
    }, 60);
  }
  function mountCards() {
    const first=Math.max(0,index-12), nearby=filtered.slice(first,index+13);
    const ids = nearby.map(p => p.id).join(',');
    if (ids === renderedIds) return;
    renderedIds = ids;
    track.replaceChildren(...nearby.map((p,offset) => {
      const i=first+offset, card = cardFor(p); card.dataset.index = String(i);
      card.setAttribute('aria-label', `Select ${p.title}, coaster ${i+1} of ${filtered.length}`);
      return card;
    }));
  }
  function pose(i) {
    const d = i - visualPosition, a = Math.abs(d);
    return { i, x:stageWidth/2+d*cardGap, y:stageHeight/2+Math.min(a,3)*12,
      scale:1-Math.min(a,4)*.105, turn:clamp(d*2.5,-8,8)*Math.PI/180,
      opacity:clamp(4-a,0,1), distance:a };
  }
  function paintDisc(p, opacity = p.opacity) {
    if (opacity <= 0) return;
    const product = filtered[p.i], sprite = sprites.get(product.id), diameter = discWidth*p.scale;
    context.save(); context.globalAlpha = opacity;
    context.translate(p.x,p.y); context.rotate(p.turn);
    if (sprite) {
      const outside = diameter*20/640;
      context.drawImage(sprite,-diameter/2-outside,-diameter/2-outside,diameter+outside*2,diameter+outside*2);
    } else {
      context.fillStyle = '#f7edda'; context.beginPath(); context.arc(0,0,diameter/2,0,Math.PI*2); context.fill();
      context.fillStyle = '#667065'; context.font = '14px Arial'; context.textAlign = 'center';
      context.fillText(`Original No. ${product.number}`,0,-6); context.fillText(imageFailures.has(product.id)?'Artwork unavailable':'Loading artwork…',0,16);
    }
    context.restore();
  }
  function drawFan() {
    if (!stageWidth || !filtered.length) return;
    context.clearRect(0,0,stageWidth,stageHeight);
    const left = Math.floor(visualPosition), right = Math.min(left+1,filtered.length-1);
    const cards = [];
    for(let i=Math.max(0,Math.floor(visualPosition)-4);i<=Math.min(filtered.length-1,Math.ceil(visualPosition)+4);i++) cards.push(pose(i));
    cards.sort((a,b)=>b.distance-a.distance);
    for(const p of cards) if(p.i!==left&&p.i!==right) paintDisc(p);
    const a=pose(left), b=pose(right), fraction=visualPosition-left;
    if(left===right) paintDisc(a);
    else {
      // At rest, paint the foreground coaster last in full. Clipping it to
      // its neighbor's face left that neighbor's shadow as a thin outline.
      const t=clamp((fraction-.25)/.5,0,1), front=1-t*t*(3-2*t);
      if(front===1){paintDisc(b);paintDisc(a);}
      else {
        paintDisc(a);paintDisc(b);
        if(front>0){
          context.save();context.beginPath();
          context.arc(b.x,b.y,discWidth*b.scale*(.5+20/640),0,Math.PI*2);
          context.clip();paintDisc(a,front);context.restore();
        }
      }
    }
  }
  function layoutCards(offset=0, position=index) {
    visualPosition=clamp(position-offset/Math.max(1,cardGap)*.62,0,Math.max(0,filtered.length-1));
    stage.dataset.visualIndex=String(visualPosition); drawFan();
  }
  function updateSelection() {
    const product=filtered[index]; if(!product)return;
    mountCards();
    selectedId=product.id;stage.dataset.selectedId=product.id;stage.dataset.index=String(index);
    for(const card of track.children) {
      const i=Number(card.dataset.index), d=i-index, p=pose(i);
      card.dataset.center=String(i===index);card.hidden=Math.abs(d)>3;
      card.style.transform=`translate(-50%,-50%) translate(${d*cardGap}px,${Math.min(Math.abs(d),3)*12}px) scale(${p.scale})`;
      card.style.zIndex=String(20-Math.abs(d)*3);
    }
    warmNearby();
    $('selected-number').textContent=`${hasFilters ? (matchingIds.has(product.id) ? 'MATCH · ' : 'MORE ORIGINALS · ') : ''}ORIGINAL No. ${product.number}`;
    $('selected-title').textContent=product.title;
    $('position').max=String(filtered.length-1);$('position').value=String(index);
    $('position').setAttribute('aria-valuetext',`${index+1} of ${filtered.length}: ${product.title}`);
    $('position-current').textContent=String(index+1).padStart(2,'0');$('position-total').textContent=filtered.length;
    $('previous').disabled=index===0;$('next').disabled=index===filtered.length-1;updateSaveButtons();
  }
  function renderFan() { mountCards();measureStage();layoutCards(0,index);updateSelection(); }
  function select(nextIndex,speak=true) {
    stopGlide();if(!filtered.length)return;
    index=clamp(nextIndex,0,filtered.length-1);selectedId=filtered[index].id;renderFan();persistPosition();
    if(speak)announce(`${index+1} of ${filtered.length}. ${filtered[index].title}`);
  }
  function followPosition(position) {
    layoutCards(0, position);
    const nearest = Math.round(visualPosition);
    if (nearest !== index) { index = nearest; updateSelection(); }
  }
  function cancelDrag() {
    const active = drag; drag = null;
    cancelAnimationFrame(dragFrame); dragFrame = 0;
    if (active && stage.hasPointerCapture(active.id)) stage.releasePointerCapture(active.id);
    stage.classList.remove('dragging');
    persistPosition(); scheduleSpriteWork();
  }
  let hoverTimer = null, hoverDirection = 0, hoverVelocity = 0;
  function stopGlide() {
    clearTimeout(hoverTimer); hoverTimer = null; hoverDirection = 0; hoverVelocity = 0;
    cancelAnimationFrame(glideFrame); glideFrame = 0;
    activeGlide = false; stage.classList.remove('animating');
    if (filtered.length && !lifecycle.signal.aborted) { index = Math.round(visualPosition); updateSelection(); persistPosition(); }
  }
  function coast(velocity) {
    stopGlide();
    const start = visualPosition;
    const target = clamp(Math.round(start + velocity * 420), 0, filtered.length - 1);
    const distance = target - start;
    if (surface.classList.contains('less-motion') || Math.abs(distance) < .0001) {
      followPosition(target); persistPosition(); scheduleSpriteWork(); return;
    }
    const duration = Math.abs(velocity) > .0002 && distance * velocity > 0
      ? clamp(2 * Math.abs(distance / velocity), 420, 1500) : 380;
    const slope = distance * velocity > 0 ? clamp(velocity * duration / distance, 0, 2.5) : 0;
    const began = performance.now();
    activeGlide = true; stage.classList.add('animating');
    const frame = now => {
      const t = Math.min(1, (now - began) / duration);
      const eased = slope*t + (10-6*slope)*t**3 + (-15+8*slope)*t**4 + (6-3*slope)*t**5;
      followPosition(start + distance * eased);
      if (t < 1) glideFrame = requestAnimationFrame(frame);
      else {
        followPosition(target);
        activeGlide = false; glideFrame = 0; stage.classList.remove('animating');
        persistPosition(); scheduleSpriteWork();
        announce(`${index + 1} of ${filtered.length}. ${filtered[index].title}`);
      }
    };
    glideFrame = requestAnimationFrame(frame);
  }
  const hoverCapable = matchMedia('(hover: hover) and (pointer: fine)');
  function endHover() {
    const velocity = hoverVelocity, running = hoverDirection && activeGlide;
    clearTimeout(hoverTimer); hoverTimer = null;
    if (running) coast(velocity);
    else hoverDirection = 0;
  }
  stage.addEventListener('pointermove', e => {
    if (e.pointerType !== 'mouse' || !hoverCapable.matches || drag || e.buttons || surface.classList.contains('less-motion')) return;
    const bounds = stage.getBoundingClientRect(), fraction = (e.clientX - bounds.left) / bounds.width;
    const direction = fraction < .22 ? -1 : fraction > .78 ? 1 : 0;
    if (!direction) { endHover(); return; }
    const strength = clamp(direction < 0 ? (.22-fraction)/.22 : (fraction-.78)/.22, 0, 1);
    if (hoverDirection === direction) { hoverVelocity = direction * (.0006 + strength * .0024); return; }
    stopGlide(); hoverDirection = direction;
    hoverVelocity = direction * (.0006 + strength * .0024);
    hoverTimer = setTimeout(() => {
      hoverTimer = null; activeGlide = true; stage.classList.add('animating');
      let last = performance.now();
      const frame = now => {
        if (!hoverDirection || drag || lifecycle.signal.aborted) return;
        const dt = Math.min(48, now-last); last = now;
        followPosition(visualPosition + hoverVelocity * dt);
        glideFrame = requestAnimationFrame(frame);
      };
      glideFrame = requestAnimationFrame(frame);
    }, 240);
  });
  stage.addEventListener('pointerleave', () => { if (!drag) endHover(); });
  function dragPosition(active, dx) {
    return clamp(active.start - dx / Math.max(1, cardGap), 0, filtered.length - 1);
  }
  function drawDragFrame() {
    dragFrame = 0;
    if (drag?.axis === 'x') followPosition(drag.position);
  }
  stage.addEventListener('pointerdown', e => {
    if (!e.isPrimary || drag) { cancelDrag(); return; }
    if (e.button !== 0) return;
    if (e.pointerType === 'mouse') e.preventDefault();
    const caughtGlide = Boolean(activeGlide);
    stopGlide();
    cancelAnimationFrame(imagePaintFrame); imagePaintFrame = 0;
    suppressClickUntil = 0;
    stage.classList.add('dragging');
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, start: visualPosition, position: visualPosition, renderVelocity: 0, frameTime: performance.now(), caughtGlide, axis: '', samples: [{ x: e.clientX, t: e.timeStamp }] };
  });
  stage.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
    if (!drag.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
      // Let the browser own vertical page scrolling; capture horizontal drags only.
      drag.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (drag.axis === 'x') stage.setPointerCapture(e.pointerId);
    }
    if (!drag.axis) return;
    suppressClickUntil = performance.now() + 350;
    if (drag.axis !== 'x') return;
    e.preventDefault();
    drag.samples.push({ x: e.clientX, t: e.timeStamp });
    drag.samples = drag.samples.slice(-12);
    drag.position = dragPosition(drag, dx);
    if (!dragFrame) {
      drag.frameTime = performance.now();
      dragFrame = requestAnimationFrame(drawDragFrame);
    }
  });
  stage.addEventListener('pointerup', e => {
    if (!drag || e.pointerId !== drag.id) return;
    const active = drag;
    if (active.axis === 'x') {
      const velocity = gestureVelocity(active.samples, e.timeStamp);
      suppressClickUntil = performance.now() + 350;
      followPosition(dragPosition(active, e.clientX - active.x));
      cancelDrag();
      coast(clamp(-velocity / Math.max(1, cardGap), -.018, .018));
    } else {
      if (active.caughtGlide) suppressClickUntil = performance.now() + 350;
      cancelDrag();
      coast(0);
    }
  });
  stage.addEventListener('pointercancel', () => {
    suppressClickUntil = performance.now() + 350;
    cancelDrag();
  });
  stage.addEventListener('lostpointercapture', e => {
    // Touch starts with implicit capture on the artwork. Transferring it to the
    // stage bubbles a lost-capture event from that child; the drag is still live.
    if (e.target === stage && drag?.id === e.pointerId && !stage.hasPointerCapture(e.pointerId)) cancelDrag();
  });
  const activateCard = next => { if (next === index && options.onOpen) { stopGlide(); options.onOpen(filtered[index]); } else select(next); };
  stage.addEventListener('click', e => {
    if (performance.now() < suppressClickUntil) { e.preventDefault(); return; }
    const card=e.target.closest('.disc');
    if(card){activateCard(Number(card.dataset.index));return;}
    const bounds=stage.getBoundingClientRect(), x=e.clientX-bounds.left, y=e.clientY-bounds.top;
    const visible=[];for(let i=Math.max(0,Math.floor(visualPosition)-3);i<=Math.min(filtered.length-1,Math.ceil(visualPosition)+3);i++)visible.push(pose(i));
    visible.sort((a,b)=>a.distance-b.distance);
    const hit=visible.find(p=>Math.hypot(x-p.x,y-p.y)<=discWidth*p.scale/2);
    if(hit)activateCard(hit.i);
  });
  stage.addEventListener('keydown', e => {
    const steps = { ArrowLeft: -1, ArrowRight: 1, PageUp: -3, PageDown: 3 };
    if (e.key in steps) { e.preventDefault(); select(index + steps[e.key]); }
    if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); select(e.key === 'Home' ? 0 : filtered.length - 1); }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateCard(index); }
  });
  $('previous').onclick = () => select(index - 1);
  $('next').onclick = () => select(index + 1);
  $('position').oninput = e => select(Number(e.target.value));
  measureStage();
  const resizeObserver = new ResizeObserver(entries => {
    const width = entries[0].contentRect.width;
    if (!width) return;
    const priorGap = cardGap;
    measureStage(width); drawFan();
    if (priorGap !== cardGap) {
      stopGlide();
      cancelAnimationFrame(scrollFrame); scrollFrame = requestAnimationFrame(() => layoutCards(0, index));
    }
  });
  resizeObserver.observe(stage);
  function updateSaveButtons() {
    const update = (button, product, includeIcon = true) => {
      const active = favorites.has(product?.id);
      button.setAttribute('aria-pressed', String(active));
      button.innerHTML = '<svg class="action-icon" aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 21s-9-5.5-9-12a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-9 12-9 12Z"/></svg><span>Favorites</span>';
      button.setAttribute('aria-label', active ? 'Remove from favorites' : 'Add to favorites');
    };
    update($('save-selected'), filtered[index]);
    for (const [id, product] of [['add-selected', filtered[index]]]) {
      const button = $(id);
      if (!button) continue;
      button.hidden = !options.onAdd;
      button.disabled = !product || !product.available;
      button.textContent = product?.available ? 'Add to Mix' : 'Unavailable';
    }
    $('grid').querySelectorAll('.grid-save').forEach(b => {
      const active = favorites.has(b.dataset.id);
      b.setAttribute('aria-pressed', String(active)); b.textContent = active ? '♥' : '♡';
      b.setAttribute('aria-label', `${active ? 'Remove' : 'Save'} ${byId.get(b.dataset.id).title} ${active ? 'from' : 'to'} favorites`);
    });
  }
  function toggleFavorite(product) {
    if (!product) return;
    const active = favorites.has(product.id);
    if (active) favorites.delete(product.id); else favorites.add(product.id);
    try { localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favorites])); } catch { storageNotice(); }
    updateSaveButtons(); renderFavorites();
    announce(`${product.title} ${active ? 'removed from' : 'saved to'} favorites.`);
  }
  $('save-selected').onclick = () => toggleFavorite(filtered[index]);
  const addToMix = product => {
    if (!product?.available || !options.onAdd) return;
    if (options.onAdd(product)) announce(`${product.title} added to your mix.`);
  };
  if ($('add-selected')) $('add-selected').onclick = () => addToMix(filtered[index]);
  function renderFavorites() {
    $('favorites-total').textContent = favorites.size;
    $('tray-count').textContent = favorites.size;
    $('favorites-empty').hidden = favorites.size > 0;
    if (options.renderFavorites) {
      options.renderFavorites([...favorites].map(id => byId.get(id)).filter(Boolean), $('favorites-tray'), toggleFavorite);
      return;
    }
    const items = [...favorites].map(id => {
      const p = byId.get(id), item = document.createElement('div');
      item.className = 'favorite-item'; item.setAttribute('role', 'listitem');
      const button = document.createElement('button');
      button.className = 'favorite-open'; button.setAttribute('aria-label', `Select saved ${p.title}`);
      const caption = document.createElement('span'); caption.className = 'favorite-caption'; caption.textContent = p.title;
      button.append(artwork(p, 160), caption);
      button.onclick = () => {
        if (!filtered.some(item => item.id === p.id)) clearFilters();
        select(filtered.findIndex(item => item.id === p.id));
        setMode('fan');
      };
      const remove = document.createElement('button');
      remove.className = 'favorite-remove'; remove.textContent = '×'; remove.setAttribute('aria-label', `Remove ${p.title} from favorites`);
      remove.onclick = () => { toggleFavorite(p); $('favorites-section').focus({ preventScroll: true }); };
      item.append(button, remove); return item;
    });
    $('favorites-tray').replaceChildren(...items);
  }
  $('favorites-jump').onclick = () => { $('favorites-section').scrollIntoView({ behavior: surface.classList.contains('less-motion') ? 'instant' : 'smooth', block: 'start' }); $('favorites-section').focus({ preventScroll: true }); };
  listen(window, 'storage', e => {
    if (e.key !== FAVORITES_KEY) return;
    try { const ids = JSON.parse(e.newValue || '[]'); if (Array.isArray(ids)) favorites = new Set(ids.filter(id => byId.has(id))); } catch { return; }
    updateSaveButtons(); renderFavorites();
  });
  function renderGrid() {
    if (options.renderGrid) {
      options.renderGrid(filtered, $('grid'));
      return;
    }
    const items = filtered.map((p, i) => {
      const card = document.createElement('article'); card.className = 'grid-card'; card.dataset.id = p.id; card.dataset.selected = String(p.id === selectedId);
      const button = document.createElement('button'); button.className = 'grid-open'; button.setAttribute('aria-label', `Select ${p.title}`);
      const number = document.createElement('span'); number.className = 'grid-number'; number.textContent = `ORIGINAL No. ${p.number}`;
      const title = document.createElement('span'); title.className = 'grid-title'; title.textContent = p.title;
      button.append(artwork(p, 320), number, title);
      button.onclick = () => { select(i); $('grid').querySelectorAll('.grid-card').forEach(c => c.dataset.selected = String(c.dataset.id === p.id)); };
      const save = document.createElement('button'); save.className = 'grid-save'; save.dataset.id = p.id; save.onclick = () => toggleFavorite(p);
      card.append(button, save); return card;
    });
    $('grid').replaceChildren(...items); updateSaveButtons();
  }
  function setMode(next) {
    stopGlide(); cancelDrag(false); mode = next;
    $('fan-view').hidden = next !== 'fan' || !filtered.length;
    $('grid-view').hidden = next !== 'grid' || !filtered.length;
    $('grid-toggle').setAttribute('aria-expanded', String(next === 'grid'));
    if (next === 'grid') { renderGrid(); requestAnimationFrame(() => { const selected = $('grid').querySelector('[data-selected="true"] .grid-open'); selected?.focus({ preventScroll: true }); selected?.scrollIntoView({ block: 'center' }); }); }
    else { renderFan(); stage.focus({ preventScroll: true }); stage.scrollIntoView({ block: 'center' }); }
    persistPosition();
  }
  $('grid-toggle').onclick = () => setMode('grid');
  $('return-to-bin').onclick = () => setMode('fan');
  $('search').addEventListener('input', e => { clearTimeout(searchTimer); const value = e.target.value.slice(0, 200); searchTimer = setTimeout(() => changeFilters(subject, theme, value), 120); });
  $('theme-filter').onchange = e => changeFilters(subject, e.target.value, query);
  $('clear-filters').onclick = clearFilters;
  $('empty-clear').onclick = () => { clearFilters(); $('search').focus(); };
  renderFavorites(); applyFilters();
  surface.dataset.ready = 'true';
  return {
    isFavorite(variantId) { const product = catalog.find(p => p.variantId === String(variantId)); return product ? favorites.has(product.id) : false; },
    toggleFavorite(variantId) { const product = catalog.find(p => p.variantId === String(variantId)); if (product) toggleFavorite(product); },
    destroy() {
    lifecycle.abort(); resizeObserver.disconnect();
    clearTimeout(searchTimer); clearTimeout(warmTimer); stopGlide(); pauseSpriteWork(); cancelAnimationFrame(imagePaintFrame); pendingSprites.clear(); sprites.clear(); cancelAnimationFrame(dragFrame); cancelAnimationFrame(scrollFrame); cardCache.clear();
  } };
  }
  window.BullseyeRecordBin = { mount };
  if (!document.documentElement.hasAttribute('data-record-bin-manual-mount') && document.getElementById('stage') && document.getElementById('catalog-error')) mount();
})();
